import { useMemo, useRef, useState, type DragEvent } from "react";
import {
  Download,
  FileText,
  FolderOpen,
  Loader2,
  ShieldCheck,
  Trash2,
  Upload,
  UploadCloud,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Field } from "@/components/ui/Label";
import { Dialog } from "@/components/ui/Dialog";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  downloadDocumentUrl,
  useDeleteDocument,
  useDocuments,
  useUploadDocument,
  type DocumentType,
  type StoredDocument,
} from "@/hooks/useDocuments";

interface FolderDef {
  id: string;
  title: string;
  body: string;
  types: DocumentType[];
}

const folders: FolderDef[] = [
  {
    id: "formation",
    title: "Formation papers",
    body: "Articles of organization, operating agreement, EIN letter, BOI confirmation.",
    types: ["Formation", "OperatingAgreement", "EinLetter", "BoiConfirmation"],
  },
  {
    id: "contracts",
    title: "Contracts & MSA",
    body: "Client master service agreements, statements of work, NDAs.",
    types: ["Msa", "Sow"],
  },
  {
    id: "tax",
    title: "Tax documents",
    body: "Returns, K-1s, 1099s, prior-year workpapers.",
    types: ["PriorYearTaxReturn", "W9Inbound", "W9Outbound"],
  },
  {
    id: "receipts",
    title: "Receipts",
    body: "Tagged to expense transactions; required for any deduction over $75.",
    types: ["Receipt"],
  },
  {
    id: "payroll",
    title: "Payroll & HR",
    body: "Paystubs, W-4s, I-9s, kid hire documentation, time logs.",
    types: ["Paystub", "PayrollStub", "EmploymentAgreement"],
  },
  {
    id: "compliance",
    title: "Compliance & insurance",
    body: "Annual reports, BOI updates, insurance policies, COIs, business licenses.",
    types: ["StateAnnualReportReceipt", "Coi", "InsurancePolicy", "BusinessLicense"],
  },
];

const docTypeOptions: { value: DocumentType; label: string }[] = [
  { value: "Formation", label: "Formation papers" },
  { value: "EinLetter", label: "EIN letter" },
  { value: "OperatingAgreement", label: "Operating agreement" },
  { value: "BoiConfirmation", label: "BOI confirmation" },
  { value: "Msa", label: "MSA" },
  { value: "Sow", label: "SOW" },
  { value: "W9Inbound", label: "W-9 inbound" },
  { value: "W9Outbound", label: "W-9 outbound" },
  { value: "Coi", label: "Certificate of insurance" },
  { value: "InsurancePolicy", label: "Insurance policy" },
  { value: "BusinessLicense", label: "Business license" },
  { value: "StateAnnualReportReceipt", label: "State annual report" },
  { value: "PriorYearTaxReturn", label: "Tax return" },
  { value: "Paystub", label: "Paystub" },
  { value: "Receipt", label: "Receipt" },
  { value: "BankStatement", label: "Bank statement" },
  { value: "RetirementPlanDocument", label: "Retirement plan doc" },
  { value: "PayrollStub", label: "Payroll stub" },
  { value: "EmploymentAgreement", label: "Employment agreement" },
  { value: "PhotoRelease", label: "Photo release" },
  { value: "Other", label: "Other" },
];

export function DocumentsPage() {
  const all = useDocuments();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const counts = useMemo(() => countByFolder(all.data ?? []), [all.data]);

  return (
    <>
      <PageHeader
        eyebrow="Documents"
        title="Receipts, contracts, formation papers"
        description="Stored in your private vault. Drag a file anywhere on this page to upload, or use the button."
        actions={
          <Button onClick={() => setUploadOpen(true)}>
            <Upload className="h-4 w-4" />
            Upload
          </Button>
        }
      />

      <DropZone
        onFiles={(files) => {
          if (files.length > 0) {
            setPendingFile(files[0]);
            setUploadOpen(true);
          }
        }}
      />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Document hygiene
          </CardTitle>
          <CardDescription>
            Why this matters — under audit, the IRS will deny any deduction that can&apos;t be
            reconstructed from contemporaneous records.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>· Keep digital copies indefinitely; physical only as a backup.</p>
          <p>· Tag receipts to transactions in the Money page; the audit trail is preserved.</p>
          <p>
            · Sensitive documents (operating agreement, EIN letter) live in this private vault —
            not in your personal cloud.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {folders.map((f) => (
          <FolderCard
            key={f.id}
            folder={f}
            count={counts[f.id] ?? 0}
            documents={(all.data ?? []).filter((d) => f.types.includes(d.documentType))}
            loading={all.isLoading}
          />
        ))}
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>All documents</CardTitle>
          <CardDescription>
            {all.data?.length ?? 0} files in your vault. Click a file to download.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentTable docs={all.data ?? []} loading={all.isLoading} />
        </CardContent>
      </Card>

      <UploadDialog
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          setPendingFile(null);
        }}
        initialFile={pendingFile}
      />
    </>
  );
}

function countByFolder(docs: StoredDocument[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const f of folders) {
    out[f.id] = docs.filter((d) => f.types.includes(d.documentType)).length;
  }
  return out;
}

function FolderCard({
  folder,
  count,
  documents,
  loading,
}: {
  folder: FolderDef;
  count: number;
  documents: StoredDocument[];
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <FolderOpen className="h-4 w-4 text-primary" />
          {folder.title}
        </CardTitle>
        <CardDescription>{folder.body}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-xs text-muted-foreground">
        <Badge tone={count === 0 ? "neutral" : "success"}>
          <FileText className="mr-1 h-3 w-3" />
          {loading ? "…" : `${count} file${count === 1 ? "" : "s"}`}
        </Badge>
        {documents.slice(0, 3).map((d) => (
          <div key={d.id} className="flex items-center justify-between truncate">
            <a
              href={downloadDocumentUrl(d.id)}
              className="truncate text-foreground/80 hover:text-primary"
              title={d.fileName}
            >
              {d.fileName}
            </a>
            <span className="ml-2 shrink-0">{formatBytes(d.sizeBytes)}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function DocumentTable({ docs, loading }: { docs: StoredDocument[]; loading: boolean }) {
  const del = useDeleteDocument();
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }
  if (docs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nothing uploaded yet. Drag a file onto the dashed area above to get started.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="py-2 pr-3">File</th>
            <th className="py-2 pr-3">Type</th>
            <th className="py-2 pr-3">Size</th>
            <th className="py-2 pr-3">Uploaded</th>
            <th className="py-2 pr-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id} className="border-b border-border/60 last:border-b-0">
              <td className="py-2 pr-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-medium">{d.fileName}</span>
                </div>
                {d.description && (
                  <div className="text-xs text-muted-foreground">{d.description}</div>
                )}
              </td>
              <td className="py-2 pr-3 text-muted-foreground">{humanType(d.documentType)}</td>
              <td className="py-2 pr-3 text-muted-foreground">{formatBytes(d.sizeBytes)}</td>
              <td className="py-2 pr-3 text-muted-foreground">
                {new Date(d.createdAt).toLocaleDateString()}
              </td>
              <td className="py-2 pr-3">
                <div className="flex items-center gap-1">
                  <a href={downloadDocumentUrl(d.id)} target="_blank" rel="noreferrer">
                    <Button variant="ghost" size="sm">
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </a>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={del.isPending}
                    onClick={() => {
                      if (confirm(`Delete "${d.fileName}"? This cannot be undone.`)) {
                        del.mutate(d.id);
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DropZone({ onFiles }: { onFiles: (files: File[]) => void }) {
  const [hover, setHover] = useState(false);
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setHover(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    onFiles(files);
  };
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={onDrop}
      className={
        "mb-6 flex items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 text-sm text-muted-foreground transition-colors " +
        (hover ? "border-primary/70 bg-primary/5" : "border-border")
      }
    >
      <UploadCloud className="h-5 w-5" />
      Drop files here to upload, or use the Upload button. PDFs, images, Office docs up to 50 MB.
    </div>
  );
}

function UploadDialog({
  open,
  onClose,
  initialFile,
}: {
  open: boolean;
  onClose: () => void;
  initialFile: File | null;
}) {
  const upload = useUploadDocument();
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(initialFile);
  const [docType, setDocType] = useState<DocumentType>("Other");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");

  const reset = () => {
    setFile(null);
    setDocType("Other");
    setDescription("");
    setTags("");
    if (fileInput.current) fileInput.current.value = "";
  };

  const submit = async () => {
    if (!file) return;
    await upload.mutateAsync({
      file,
      documentType: docType,
      description: description || undefined,
      tags: tags || undefined,
    });
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Upload document"
      description="File goes straight into your private vault. You can re-categorize anytime."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!file || upload.isPending}>
            {upload.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Upload
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="File">
          <Input
            ref={fileInput}
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file && (
            <p className="text-xs text-muted-foreground">
              {file.name} · {formatBytes(file.size)}
            </p>
          )}
          {initialFile && file === initialFile && (
            <p className="text-xs text-muted-foreground">From drag-drop · {initialFile.name}</p>
          )}
        </Field>
        <Field label="Document type">
          <Select value={docType} onChange={(e) => setDocType(e.target.value as DocumentType)}>
            {docTypeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Description" hint="Optional context shown in the table.">
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Acme Corp 2026 MSA, signed 04/01"
          />
        </Field>
        <Field label="Tags" hint="Comma-separated. Used for full-text search later.">
          <Input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="acme,client,2026"
          />
        </Field>
        {upload.isError && (
          <p className="text-xs text-destructive">
            Upload failed. Verify the API is running and try again.
          </p>
        )}
      </div>
    </Dialog>
  );
}

function humanType(t: DocumentType): string {
  return docTypeOptions.find((o) => o.value === t)?.label ?? t;
}

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
